// Utility functions for working with caret/selection positions in a contentEditable element
// High-level API inspired by https://stackoverflow.com/a/6249344/444255

export function getSelectionOffset(container: HTMLElement): [number, number] {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
        return [0, 0];
    }
    const range = selection.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(container);
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const end = start + range.toString().length;
    return [start, end];
}


export function escapeHtml(s: string) {
    return s
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");
}


// Преобразование текста в безопасный HTML c <br> и сериями &nbsp;
export function textToHtml(input: string) {
    input = escapeHtml(input)
    // таб -> 4 пробела
    input = input.replace(/\t/g, "    ");
    input = input.replace(/\r\n|\n|\r/g, "<br>");  // переносы -> <br>
    // Серии из 2+ пробелов рендерим как пары "&nbsp; " и при нечётном количестве — завершающий "&nbsp;"
    // Это даёт ровно исходное число видимых пробелов:
    // 2 пробела => "&nbsp; "; 3 пробела => "&nbsp; &nbsp;"; 4 => "&nbsp; &nbsp; "; и т.д.
    input = input.replace(/ {2,}/g, (m) => {
        const pairs = Math.floor(m.length / 2);
        const odd = (m.length % 2) === 1;
        return "&nbsp; ".repeat(pairs) + (odd ? "&nbsp;" : "");
    });
    return input
}

function traverseTextNodes(node: Node, charCount: { count: number }, range: Range): Range {
    if (!range) {
        range = document.createRange();
    }

    if (charCount.count === 0) {
        range.setEnd(node, 0);
        return range;
    }

    if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        if (textNode.textContent) {
            if (textNode.textContent.length < charCount.count) {
                charCount.count -= textNode.textContent.length;
            } else {
                range.setEnd(textNode, charCount.count);
                charCount.count = 0;
            }
        }
    } else {
        for (let i = 0; i < node.childNodes.length; i++) {
            range = traverseTextNodes(node.childNodes[i], charCount, range);
            if (charCount.count === 0) {
                break;
            }
        }
    }
    return range;
}

export function setSelectionOffset(container: HTMLElement, start: number, end?: number): void {
    if (typeof window === 'undefined' || !container) return;

    if (end == null) end = start;

    const selection = window.getSelection();
    if (!selection) return;

    const startRange = traverseTextNodes(container, { count: start }, document.createRange());
    const endRange = traverseTextNodes(container, { count: end }, document.createRange());

    const range = document.createRange();
    range.setStart(startRange.endContainer, startRange.endOffset);
    range.setEnd(endRange.endContainer, endRange.endOffset);

    selection.removeAllRanges();
    selection.addRange(range);
}

type Options = {
    blockSep?: string;            // разделитель между БЛОКАМИ и <br>
    collapseEmptyLines?: boolean; // схлопывать >=3 \n → \n\n в обычных сегментах
};

// Узлы, считающиеся «преформатированными» без CSS
const PRE_TAGS = new Set(['PRE', 'CODE', 'TEXTAREA']);
// Блочные теги (жёсткий список)
const BLOCK_TAGS = new Set([
    'ADDRESS','ARTICLE','ASIDE','BLOCKQUOTE','DIV','DL','DT','DD','FIELDSET','FIGCAPTION','FIGURE','FOOTER',
    'FORM','H1','H2','H3','H4','H5','H6','HEADER','HR','LI','MAIN','NAV','OL','P','SECTION','TABLE','UL',
    'TR' // строка таблицы — всегда заканчиваем переводом
]);

// Символы, перед которыми пробел не вставляется
const NO_LEADING_SPACE = /[),.;:!?%}\]\)>]/;

type Segment = { t: string; pre: boolean };

export function htmlVisibleText(root: HTMLElement, opts: Options = {}): string {
    const blockSep = opts.blockSep ?? '\n';
    const collapseEmptyLines = opts.collapseEmptyLines ?? false;

    const segs: Segment[] = [];
    let cur: Segment = { t: '', pre: false };
    // cur.t is built a char at a time; reading its end (endsWith, a /…$/ regex)
    // flattens the whole string, which made extraction quadratic in the text's length.
    // Its last char is therefore tracked here, and the end is only rewritten when it
    // really holds the characters being trimmed.
    let last = '';
    let preDepth = 0;

    // Состояние для обычного режима
    let pendingSpace = false; // отложенный пробел
    let atLineStart = true;   // в начале строки (после \n или пусто)

    const flushCur = () => { if (cur.t) { segs.push(cur); cur = { t: '', pre: preDepth > 0 }; last = ''; } };
    const ensureMode = (pre: boolean) => {
        if (cur.pre !== pre) { flushCur(); cur.pre = pre; }
    };

    const pushChar = (ch: string) => {
        cur.t += ch;
        last = ch[ch.length - 1];
        atLineStart = ch === '\n';
    };

    const endsWith = (s: string) => s.length === 1 ? last === s : cur.t.endsWith(s);

    const trimEnd = (chars: string) => {
        if (!chars.includes(last)) return;
        let end = cur.t.length;
        while (end > 0 && chars.includes(cur.t[end - 1])) end--;
        cur.t = cur.t.slice(0, end);
        last = cur.t[end - 1] ?? '';
    };

    const pushNL = () => {
        ensureMode(preDepth > 0); // \n попадает в текущий режим
        if (!endsWith(blockSep)) {
            // убирать хвостовые пробелы только в обычном режиме
            if (!cur.pre) trimEnd(' ');
            pushChar(blockSep);
        }
        pendingSpace = false;
    };

    // Добавить перевод строки без дедупликации (для явных <br> и пустых блоков)
    const pushNLAlways = () => {
        ensureMode(preDepth > 0);
        if (!cur.pre) trimEnd(' ');
        pushChar(blockSep);
        pendingSpace = false;
    };

    const emitSpaceIfNeededBefore = (nextVisibleChar: string) => {
        if (!pendingSpace) return;
        if (atLineStart) { pendingSpace = false; return; }
        if (NO_LEADING_SPACE.test(nextVisibleChar)) { pendingSpace = false; return; }
        if (last !== ' ') pushChar(' ');
        pendingSpace = false;
    };

    const textRunPre = (text: string) => {
        ensureMode(true);
        // Ничего не трогаем: и табы, и множественные пробелы, и \n — как есть
        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            if (ch === '\n') pendingSpace = false; // не влияет на pre, просто сброс
            pushChar(ch);
        }
    };

    const textRunNormal = (text: string) => {
        ensureMode(false);
        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            if (ch === '\u00A0') { // NBSP — видимый пробел, не схлопывается
                // Если есть отложенный обычный пробел — сначала отрисуем его явно
                if (pendingSpace && !atLineStart) {
                    // не применяем NO_LEADING_SPACE для NBSP-кейса, нам нужно сохранить количество
                    pushChar(' ');
                }
                pendingSpace = false;
                // Сам NBSP тоже добавляет видимый пробел
                pushChar(' ');
                continue;
            }
            if (ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n' || ch === '\f') {
                pendingSpace = true; // схлопываем в один потенциальный пробел
                continue;
            }
            emitSpaceIfNeededBefore(ch);
            pushChar(ch);
        }
    };

    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const t = node.nodeValue ?? '';
            if (preDepth > 0) textRunPre(t); else textRunNormal(t);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const el = node as HTMLElement;
        const name = el.nodeName;

        // Явные разрывы строк должны всегда давать новую пустую строку, даже подряд
        if (name === 'BR') { pushNLAlways(); return; }

        const isPreHere = PRE_TAGS.has(name);
        if (isPreHere) preDepth++;

        const isBlock = BLOCK_TAGS.has(name);

        if (isBlock && cur.t && !endsWith(blockSep)) pushNL();

        if (name === 'TD' || name === 'TH') {
            // содержимое ячейки
            el.childNodes.forEach(walk);
            // таб после ячейки, не ломая pre/normal режим
            ensureMode(preDepth > 0);
            if (!cur.pre) trimEnd(' ');
            pushChar('\t');
            if (isPreHere) preDepth--;
            return;
        }

        if (name === 'TR') {
            el.childNodes.forEach(walk);
            // убрать хвостовые табы/пробелы и добавить перевод
            ensureMode(preDepth > 0);
            if (!cur.pre) trimEnd('\t ');
            pushNL();
            if (isPreHere) preDepth--;
            return;
        }

        const textLenBefore = cur.t.length;
        el.childNodes.forEach(walk);

        if (isBlock) {
            const producedNoVisibleText = cur.t.length === textLenBefore;
            if (producedNoVisibleText) {
                // Пустой блочный элемент — сохраняем пустую строку
                pushNLAlways();
            } else {
                // Обычный блочный элемент — сохраняем единственный перевод строки
                pushNL();
            }
        }
        if (isPreHere) preDepth--;
    };

    walk(root);
    flushCur();

    // Постобработка: нормализуем ТОЛЬКО обычные сегменты
    for (let i = 0; i < segs.length; i++) {
        const s = segs[i];
        if (s.pre) continue;

        // убрать пробелы перед переводами
        s.t = s.t.replace(/[ \t]+\n/g, '\n');

        // по желанию: схлопнуть сверхпустые строки
        if (collapseEmptyLines) s.t = s.t.replace(/\n{3,}/g, '\n\n');

        // убрать пробелы перед пунктуацией
        s.t = s.t.replace(/ +([),.;:!?%}\]\)>])/g, '$1');
    }

    // Склейка и финальный хвостовой трим только для конца всего текста,
    // не затрагивая внутренние отступы pre-сегментов
    let out = segs.map(s => s.t).join('');
    // Удаляет пробелы в конце строки, вызывает сброс положения корретки (потому что отличается очищенный текст от содержимого
    // out = out.replace(/\s+$/,'');
    return out;
}

// Обёртка для HTML-строки (без CSS — поэтому полагаемся на теги PRE/CODE/TEXTAREA)
export function htmlVisibleTextFromString(html: string, opts?: Options): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const el = (container.querySelector('[contenteditable="true"]') || container) as HTMLElement;
    return htmlVisibleText(el, opts);
}


export function getInnerTextFromMarkup(markup: string): string {
    const container = document.createElement('div');
    container.innerHTML = markup;

    let text = '';

    function traverse(node: Node) {
        // Пропускаем тултипы
        if (node instanceof HTMLElement && node.closest('.segmentation-tooltip')) {
            return;
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const t = (node as Text).textContent ?? '';
            // Preserve exact spacing: convert NBSP to regular space, do not collapse sequences
            text += t.replace(/\u00a0/g, ' ');
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.tagName === 'BR') {
                text += '\n';
            } else {
                // Рекурсивно обходим дочерние узлы
                for (let i = 0; i < el.childNodes.length; i++) {
                    traverse(el.childNodes[i]);
                }
            }
        }
    }

    // 2. Запускаем обход с кор невого контейнера
    for (let i = 0; i < container.childNodes.length; i++) {
        traverse(container.childNodes[i]);
    }

    return text;
}